import PropTypes from "prop-types";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Close, ExpandLess, ExpandMore, QueryStats, Refresh, Search } from "@mui/icons-material";
import DataTable from "shared/components/Tables/DataTable";
import { MetricasResumen } from "../MetricasResumen";
import { FiltrosGerencia } from "../FiltrosGerencia";
import { GraficosEfectividad } from "../GraficosEfectividad";
import { useGerenciaData } from "../../hooks/useGerenciaData";
import {
  counts,
  effectiveness,
  EMPTY_FILTERS,
  formatAmount,
  formatCount,
  formatPercent,
  metrics,
  recordDay,
} from "../../utils/gerenciaModel";

const tableSx = {
  overflowX: "auto",
  "& thead th > div": { fontSize: ".75rem", opacity: 1, color: "text.secondary" },
  "& tbody td": { fontSize: ".9375rem", py: 1.5 },
  "& tbody td > div": { fontSize: "inherit", color: "text.primary" },
  "& tbody td .MuiTypography-root": { fontSize: "inherit" },
};
const buttonSx = { textTransform: "none", minHeight: 40, borderRadius: 2, fontSize: ".8125rem" };
const titles = { aceptacion: "Aceptación", recaudo: "Recaudo" };
const updateDate = (date) =>
  date
    ? new Intl.DateTimeFormat("es-CO", {
        timeZone: "America/Bogota",
        dateStyle: "short",
        timeStyle: "short",
      }).format(date)
    : "Pendiente de consulta";
function RecordDetail({ record, tipo, onClose }) {
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const fields = [
    ["fecha", "Fecha"],
    ["comercio", "Comercio"],
    ["servicio", "Servicio"],
    ["pasarela", "Pasarela"],
    ["status", "Estado"],
    ...(tipo === "aceptacion"
      ? [
          ["tipopasarela", "Tipo de pasarela"],
          ["tipotarjeta", "Tipo de tarjeta"],
          ["franquicia", "Franquicia"],
          ["issuingbank", "Banco emisor"],
          ["responsepgw", "Respuesta de pasarela"],
        ]
      : [["tipo_fuente", "Tipo de fuente"]]),
  ];
  return (
    <Dialog
      open={Boolean(record)}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      aria-labelledby={`gerencia-detail-${tipo}`}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
    >
      <DialogTitle
        id={`gerencia-detail-${tipo}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          background: "linear-gradient(110deg,#12304d,#175d7a)",
          color: "#fff",
        }}
      >
        <Box sx={{ flex: 1 }}>Detalle de {titles[tipo].toLowerCase()}</Box>
        <IconButton aria-label="Cerrar detalle" onClick={onClose} sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {record && (
          <>
            <Box sx={{ my: 2 }}>
              <MetricasResumen metricas={metrics([record], tipo)} />
            </Box>
            <Box
              component="dl"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" },
                gap: 2,
                m: 0,
              }}
            >
              {fields.map(([key, label]) => (
                <Box
                  key={key}
                  sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 1.5, minWidth: 0 }}
                >
                  <Typography component="dt" variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography
                    component="dd"
                    variant="body2"
                    sx={{ m: 0, overflowWrap: "anywhere" }}
                  >
                    {key === "fecha"
                      ? recordDay(record.fecha) || "Sin información"
                      : String(record[key] ?? "Sin información")}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={buttonSx}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
RecordDetail.propTypes = {
  record: PropTypes.object,
  tipo: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};
function GerenciaSection({ tipo, isOpen, onToggle, onOpen, refreshFlag }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const data = useGerenciaData(filters, tipo, search, refreshFlag);
  const {
    datos,
    datosParaTabla,
    totalResultadosTabla,
    metricasResumen,
    opcionesFiltros,
    filtrosAplicados,
    loading,
    error,
    validation,
    updatedAt,
    recargar,
    buscarConFiltros,
    limpiarFiltros,
    limiteRegistros,
  } = data;
  const onChange = (key, value) => setFilters((old) => ({ ...old, [key]: value }));
  const clear = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
    limpiarFiltros();
  };
  const remove = (key) => {
    onChange(key, "");
    buscarConFiltros({ ...filtrosAplicados, [key]: "" });
  };
  const columns = [
    { Header: "Fecha", accessor: "date" },
    { Header: "Comercio", accessor: "commerce" },
    { Header: "Pasarela", accessor: "gateway" },
    { Header: "Transacciones", accessor: "total", align: "right" },
    { Header: "Exitosas", accessor: "success", align: "right" },
    { Header: "Fallidas", accessor: "failed", align: "right" },
    { Header: "Efectividad", accessor: "effectiveness", align: "right" },
    { Header: "Monto (COP)", accessor: "amount", align: "right" },
    { Header: "Detalle", accessor: "action" },
  ];
  const rows = datosParaTabla.map((item) => {
    const value = counts(item, tipo);
    const percent = effectiveness(value.success, value.total);
    return {
      date: recordDay(item.fecha) || "Sin información",
      commerce: item.comercio || "Sin información",
      gateway: item.pasarela || "Sin información",
      total: formatCount(value.total),
      success: formatCount(value.success),
      failed: formatCount(value.failed),
      effectiveness: (
        <Chip
          size="small"
          label={formatPercent(percent)}
          variant="outlined"
          color={
            percent == null
              ? "default"
              : percent >= 95
              ? "success"
              : percent >= 90
              ? "warning"
              : "error"
          }
        />
      ),
      amount: formatAmount(value.amount),
      action: (
        <Button
          aria-label={`Ver detalle de ${item.comercio || "registro"} del ${recordDay(item.fecha)}`}
          onClick={() => setSelected(item)}
          sx={buttonSx}
        >
          Ver detalle
        </Button>
      ),
    };
  });
  return (
    <Paper
      component="section"
      aria-label={`Resumen de ${titles[tipo]}`}
      data-testid={`gerencia-${tipo}`}
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        mb: 2.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
      }}
    >
      <Box
        onClick={(event) => {
          if (!event.target.closest("button")) onToggle();
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          cursor: "pointer",
          mb: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="h6">{titles[tipo]}</Typography>
          <Typography variant="caption" color="text.secondary">
            Actualizado: {updateDate(updatedAt)}
          </Typography>
        </Box>
        <Button
          disabled={loading}
          aria-label={`Actualizar ${titles[tipo]}`}
          startIcon={<Refresh />}
          onClick={recargar}
          sx={buttonSx}
        >
          {loading ? "Actualizando…" : "Actualizar"}
        </Button>
        <Button
          aria-expanded={isOpen}
          aria-controls={`gerencia-info-${tipo}`}
          aria-label={`${isOpen ? "Ocultar" : "Ver"} información de ${titles[tipo]}`}
          endIcon={isOpen ? <ExpandLess /> : <ExpandMore />}
          onClick={onToggle}
          sx={buttonSx}
        >
          {isOpen ? "Ocultar información" : "Ver información"}
        </Button>
      </Box>
      {loading && (
        <LinearProgress
          aria-label={`Cargando ${titles[tipo]}`}
          sx={{
            mb: 2,
            width: "100%",
            position: "relative",
            overflow: "hidden",
            "& .MuiLinearProgress-bar": { bgcolor: "#1a73e8" },
          }}
        />
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button onClick={recargar} disabled={loading} color="inherit">
              Reintentar
            </Button>
          }
        >
          {error}
          {updatedAt
            ? ". Se conserva la última consulta disponible."
            : ". No se pudieron consultar los indicadores."}
        </Alert>
      )}
      <MetricasResumen metricas={updatedAt ? metricasResumen : null} loading={loading} />
      {Object.values(filtrosAplicados).some(Boolean) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", mt: 1.5, gap: 1 }}>
          <Chip label="Resumen filtrado" size="small" color="info" variant="outlined" />
          <Button onClick={onOpen} sx={buttonSx}>
            Ver filtros aplicados
          </Button>
          <Button onClick={clear} sx={buttonSx}>
            Limpiar filtros
          </Button>
        </Box>
      )}
      {isOpen && (
        <Box id={`gerencia-info-${tipo}`}>
          <FiltrosGerencia
            filtros={filters}
            onFiltrosChange={onChange}
            onBuscar={() => buscarConFiltros()}
            onLimpiar={clear}
            onRemove={remove}
            opcionesFiltros={opcionesFiltros}
            loading={loading}
            vistaActual={tipo}
            filtrosAplicados={filtrosAplicados}
            validation={validation}
          />
          <GraficosEfectividad datos={datos} vistaActual={tipo} />
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography variant="h6">Detalle de {titles[tipo].toLowerCase()}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatCount(totalResultadosTabla)} registros coinciden con la búsqueda ·{" "}
                  {formatCount(datos.length)} registros en la consulta
                </Typography>
              </Box>
              <TextField
                label="Buscar en todos los registros"
                placeholder="Comercio, banco, respuesta…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 290 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {totalResultadosTabla > limiteRegistros && (
              <Alert severity="info" sx={{ m: 2 }}>
                La tabla muestra los {formatCount(limiteRegistros)} registros más recientes de{" "}
                {formatCount(totalResultadosTabla)} coincidencias. La búsqueda recorre todos los
                datos recibidos; los indicadores y gráficos usan todos los registros filtrados.
              </Alert>
            )}
            <Box sx={tableSx}>
              <DataTable
                table={{ columns, rows }}
                entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50, 100] }}
                canSearch={false}
                isSorted={false}
                showTotalEntries
                noEndBorder
                autoResetPage={false}
                paginationResetKey={JSON.stringify([filtrosAplicados, search])}
              />
            </Box>
            {!loading && !error && rows.length === 0 && (
              <Typography variant="body2" sx={{ p: 2.5 }}>
                {search
                  ? "No hay coincidencias para la búsqueda."
                  : "No hay registros para los filtros seleccionados."}
              </Typography>
            )}
          </Paper>
        </Box>
      )}
      <RecordDetail record={selected} tipo={tipo} onClose={() => setSelected(null)} />
    </Paper>
  );
}
GerenciaSection.propTypes = {
  tipo: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  refreshFlag: PropTypes.string,
};
export const TablaGerencia = ({ refreshFlag }) => {
  const [expanded, setExpanded] = useState(null);
  const [refresh, setRefresh] = useState(0);
  return (
    <Box sx={{ pb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2.5,
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          background: "linear-gradient(110deg,#12304d,#175d7a)",
          color: "#fff",
        }}
      >
        <QueryStats />
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="h5" color="inherit">
            Tablero gerencial
          </Typography>
          <Typography variant="body2" color="inherit" sx={{ opacity: 0.85, mt: 0.5 }}>
            Indicadores de aceptación y recaudo. Despliega cada sección para consultar su detalle.
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={() => setRefresh((value) => value + 1)}
          sx={{
            ...buttonSx,
            bgcolor: "#fff",
            color: "#174b68",
            px: 2,
            "&:hover": { bgcolor: "#edf4ff" },
          }}
        >
          Actualizar todo
        </Button>
      </Paper>
      {["aceptacion", "recaudo"].map((tipo) => (
        <GerenciaSection
          key={tipo}
          tipo={tipo}
          isOpen={expanded === tipo}
          onToggle={() => setExpanded((old) => (old === tipo ? null : tipo))}
          onOpen={() => setExpanded(tipo)}
          refreshFlag={JSON.stringify([refreshFlag, refresh])}
        />
      ))}
    </Box>
  );
};
TablaGerencia.propTypes = { refreshFlag: PropTypes.bool };
